import { createContext, useContext, useState, useEffect, useCallback, ReactNode, useRef } from 'react'
import { authService } from '@/services/auth'
import type { User, LoginFormData, AuthState, PhoneVerificationRequest, ResendCodeRequest, ValidatePhoneRequest, RegistrationDetailsData, AuthResponse } from '@/types/auth'

export type AuthEventHandler = (user?: User) => void | Promise<void>

interface AuthContextType extends AuthState {
  login: (data: LoginFormData) => Promise<AuthResponse>
  validatePhone: (data: ValidatePhoneRequest) => Promise<any>
  signUpWithDetails: (phone: string, verificationToken: string, data: RegistrationDetailsData) => Promise<AuthResponse>
  verifyPhone: (data: PhoneVerificationRequest) => Promise<string>
  resendCode: (data: ResendCodeRequest) => Promise<any>
  logout: () => Promise<void>
  clearError: () => void
  // Lifecycle handlers registration
  registerLoginHandler: (handler: AuthEventHandler) => () => void
  registerLogoutHandler: (handler: AuthEventHandler) => () => void
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>(() => {
    const hasToken = authService.isAuthenticated()
    const cachedUser = hasToken ? authService.getUserFromCache() : null

    return {
      user: cachedUser,
      isAuthenticated: hasToken,
      isLoading: true,
      error: null,
    }
  })

  // Track previous authentication state for lifecycle events
  const previousAuthState = useRef<boolean | null>(null)

  // Lifecycle handlers
  const loginHandlers = useRef<AuthEventHandler[]>([])
  const logoutHandlers = useRef<AuthEventHandler[]>([])

  // Register lifecycle handlers
  const registerLoginHandler = useCallback((handler: AuthEventHandler): (() => void) => {
    loginHandlers.current.push(handler)

    // Return unregister function
    return () => {
      loginHandlers.current = loginHandlers.current.filter(h => h !== handler)
    }
  }, [])

  const registerLogoutHandler = useCallback((handler: AuthEventHandler): (() => void) => {
    logoutHandlers.current.push(handler)

    // Return unregister function
    return () => {
      logoutHandlers.current = logoutHandlers.current.filter(h => h !== handler)
    }
  }, [])

  // Check if user is authenticated on mount
  useEffect(() => {
    const checkAuth = async () => {
      try {
        if (authService.isAuthenticated()) {
          // Load user from cache first to avoid UI flicker
          const cachedUser = authService.getUserFromCache()
          const hasValidCache = !!cachedUser && !!cachedUser.name && cachedUser.name.trim() !== ''

          if (hasValidCache) {
            setState({
              user: cachedUser,
              isAuthenticated: true,
              isLoading: false,
              error: null,
            })

            // Refresh in background (no UI blocking)
            authService.getProfile()
              .then((freshUser) => {
                authService.saveUserToCache(freshUser)
                setState({
                  user: freshUser,
                  isAuthenticated: true,
                  isLoading: false,
                  error: null,
                })
              })
              .catch(() => {
                // Keep cached user if refresh fails
              })
            return
          }

          // Fallback: fetch fresh profile if cache is missing or stale
          const freshUser = await authService.getProfile()
          authService.saveUserToCache(freshUser)
          setState({
            user: freshUser,
            isAuthenticated: true,
            isLoading: false,
            error: null,
          })
        } else {
          setState(prev => ({ ...prev, isLoading: false }))
        }
      } catch (error) {
        setState({
          user: null,
          isAuthenticated: false,
          isLoading: false,
          error: null,
        })
      }
    }

    checkAuth()
  }, [])

  // Auth lifecycle: handle login/logout events
  useEffect(() => {
    // Skip first render (initial state)
    if (previousAuthState.current === null) {
      previousAuthState.current = state.isAuthenticated
      return
    }

    // Detect logout: was authenticated, now is not
    if (previousAuthState.current === true && state.isAuthenticated === false) {
      console.log('[AuthContext] Logout detected - executing handlers')

      // Execute all logout handlers
      logoutHandlers.current.forEach(async (handler) => {
        try {
          await handler()
        } catch (error) {
          console.error('[AuthContext] Logout handler error:', error)
        }
      })
    }

    // Detect login: was not authenticated, now is
    if (previousAuthState.current === false && state.isAuthenticated === true) {
      console.log('[AuthContext] Login detected - executing handlers')

      // Execute all login handlers with user data
      loginHandlers.current.forEach(async (handler) => {
        try {
          await handler(state.user || undefined)
        } catch (error) {
          console.error('[AuthContext] Login handler error:', error)
        }
      })
    }

    // Update previous state
    previousAuthState.current = state.isAuthenticated
  }, [state.isAuthenticated])

  const login = useCallback(async (data: LoginFormData) => {
    setState(prev => ({ ...prev, isLoading: true, error: null }))

    try {
      const authData = await authService.login(data)
      // Extract user profile from auth response
      const user = authData.data.profile

      // Save to cache so components reading from cache (e.g. MarketplaceHeader) get the user immediately
      authService.saveUserToCache(user)

      setState({
        user,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      })
      return authData
    } catch (error: any) {
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: error.message,
      }))
      throw error
    }
  }, [])

  const validatePhone = useCallback(async (data: ValidatePhoneRequest) => {
    setState(prev => ({ ...prev, isLoading: true, error: null }))

    try {
      const response = await authService.sendPhoneChallenge(data.phone)
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: null,
      }))
      return response
    } catch (error: any) {
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: error.message,
      }))
      throw error
    }
  }, [])

  const signUpWithDetails = useCallback(async (phone: string, verificationToken: string, data: RegistrationDetailsData) => {
    setState(prev => ({ ...prev, isLoading: true, error: null }))

    try {
      const authData = await authService.signUpWithDetails(phone, verificationToken, data)
      // Extract user profile from auth response
      const user = authData.data.profile

      // Save to cache so components reading from cache (e.g. MarketplaceHeader) get the user immediately
      authService.saveUserToCache(user)

      setState({
        user,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      })
      return authData
    } catch (error: any) {
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: error.message,
      }))
      throw error
    }
  }, [])

  const logout = useCallback(async () => {
    setState(prev => ({ ...prev, isLoading: true }))

    try {
      await authService.logout()
      // Cache will be cleared by logout handler
      setState({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
      })
    } catch (error: any) {
      // Cache will be cleared by logout handler
      setState({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: error.message,
      })
    }
  }, [])

  const verifyPhone = useCallback(async (data: PhoneVerificationRequest): Promise<string> => {
    setState(prev => ({ ...prev, isLoading: true, error: null }))

    try {
      const verificationToken = await authService.verifyPhoneChallenge(data.phone, data.code)
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: null,
      }))
      return verificationToken
    } catch (error: any) {
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: error.message,
      }))
      throw error
    }
  }, [])

  const resendCode = useCallback(async (data: ResendCodeRequest) => {
    setState(prev => ({ ...prev, isLoading: true, error: null }))

    try {
      const response = await authService.resendPhoneChallenge(data.phone)
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: null,
      }))
      return response
    } catch (error: any) {
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: error.message,
      }))
      throw error
    }
  }, [])

  const clearError = useCallback(() => {
    setState(prev => ({ ...prev, error: null }))
  }, [])

  return (
    <AuthContext.Provider
      value={{
        ...state,
        login,
        validatePhone,
        signUpWithDetails,
        verifyPhone,
        resendCode,
        logout,
        clearError,
        registerLoginHandler,
        registerLogoutHandler,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
